import { useEffect } from 'react';
import { getLoads, getDetentionSettings } from './loadStore';
import { getDevices, getDrivers, getPositions } from './traccar';
import { sopStage } from './sop';
import { enqueue } from './outbox';
import { buildAlarmReport } from './alarmReport';

// stages the autopilot composes broker/driver messages for
const ACTIONABLE = ['Dispatched', 'At Pickup', 'Loaded', 'In Transit', 'At Delivery', 'Delivered', 'Complete'];

// straight-line miles, bumped ~25% to approximate road miles
const haversine = (a, b) => {
  const R = 3958.8, toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};
const roadMiles = (a, b) => Math.round(haversine(a, b) * 1.25);

// the next stop the truck is heading to (first geofenced stop not yet departed)
function nextTarget(load) {
  const stops = ((load.geo || {}).stops) || {};
  const ordered = Object.values(stops).sort((x, y) => (x.type === y.type ? x.seq - y.seq : x.type === 'pickup' ? -1 : 1));
  return ordered.find((s) => !s.departure && s.lat && s.lng) || null;
}

// build the ctx (truck, driver, live miles/ETA) the SOP templates fill from
function buildCtx(load, devices, drivers, positions) {
  const dev = devices.find((d) => String(d.id) === String(load.vehicleId));
  const drv = drivers.find((d) => String(d.id) === String(load.driverId));
  const pos = positions.find((p) => String(p.deviceId) === String(load.vehicleId));
  const ctx = {
    truckName: (dev && dev.name) || load.truckNumber || '',
    driverName: (drv && drv.name) || '',
    driverPhone: (drv && (drv.attributes || {}).phone) || (drv && drv.phone) || '',
  };
  const target = nextTarget(load);
  if (pos && target) ctx.miles = roadMiles({ lat: pos.latitude, lng: pos.longitude }, { lat: target.lat, lng: target.lng });
  return ctx;
}

export default function Autopilot() {
  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      try {
        const [loads] = await Promise.all([getLoads()]);
        const [devices, drivers, positions] = await Promise.all([
          getDevices().catch(() => []), getDrivers().catch(() => []), getPositions().catch(() => []),
        ]);
        if (cancelled) return;

        const now = new Date();
        const mins = now.getHours() * 60 + now.getMinutes();
        const h = now.getHours();
        const day = now.toLocaleDateString('en-CA');
        const period = h < 11 ? 'am' : h < 17 ? 'pm' : 'eve'; // 2–3 transit updates/day

        const actions = [];
        for (const load of loads) {
          if (!ACTIONABLE.includes(load.status)) continue;
          const ctx = buildCtx(load, devices, drivers, positions);
          const stage = sopStage(load, ctx);
          const broker = (load.caller && load.caller.name) || 'broker';

          // GPS gate — only act once the truck actually reached the geofence.
          const stops = (load.geo || {}).stops || {};
          const arrived = (type) => Object.values(stops).some((x) => x.type === type && x.arrival);
          const gateOpen = (key) => {
            if (key === 'arrived-shipper' || key === 'pickup-door' || key === 'loaded-photos') return arrived('pickup');
            if (key === 'arrived-delivery' || key === 'delivery-door' || key === 'pod') return arrived('delivery');
            return true; // driver-info / day-of / transit (between geofences)
          };

          // broker messages: 'auto' fire on the geofence gate; 'reply' fire once
          // the driver's logged reply fills the required fields; 'manual' are skipped.
          const di = load.driverInfo || {};
          (stage.messages || []).forEach((m) => {
            if (m.trigger === 'manual') return;
            if (!gateOpen(m.key)) return; // truck not confirmed at the geofence yet
            if (m.trigger === 'reply' && !(m.requires || []).every((k) => di[k])) return; // waiting on driver
            const sig = m.key === 'transit-update'
              ? `${load.id}:transit:${day}:${period}`          // caps at ~3/day while in transit
              : `${load.id}:${m.key}:${load.status}`;           // once per stage otherwise
            actions.push({
              sig, loadId: load.id, billNumber: load.billNumber, channel: 'broker',
              kind: m.label, subject: m.subject, body: m.body, to: broker,
            });
          });

          // the AI dispatcher texts the driver — same GPS gate
          (stage.asks || []).forEach((a) => {
            if (!gateOpen(a.key)) return; // not at the geofence yet — hold the question
            actions.push({
              sig: `${load.id}:ask:${a.key}:${load.status}`,
              loadId: load.id, billNumber: load.billNumber, channel: 'driver',
              kind: 'Ask driver', subject: `Text driver — ${load.billNumber}`, body: a.text,
              to: ctx.driverName || 'driver',
            });
          });
        }
        if (actions.length) enqueue(actions);

        // --- Alarm Report on its 4 daily slots (once per slot per day) ---
        const SLOTS = [30, 390, 750, 1110]; // 12:30a, 6:30a, 12:30p, 6:30p
        const slot = SLOTS.find((t) => mins >= t && mins < t + 5); // within the 5-min window
        if (slot != null) {
          const r = buildAlarmReport(now);
          enqueue([{
            sig: `alarm:${day}:${slot}`, loadId: null, billNumber: r.subject, channel: 'alarm',
            kind: 'Alarm Report', subject: r.subject, body: r.body, to: r.to.map((x) => x.name).join(', '),
          }]);
        }
      } catch { /* retry next round */ }
      // keep detention settings warm (used elsewhere); harmless if it fails
      getDetentionSettings().catch(() => {});
    };

    tick();
    const t = setInterval(tick, 30000); // every 30s
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return null;
}
